// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// IMPORT CSS
import "../../theme/style.scss";
import "react-quill/dist/quill.snow.css";

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CustomBreadcrumb from "../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// IMPORT SERVICE
import { AlertType } from "../../services/models/AlertType";
import { EncryptDecryptService } from "../../services/common/EncryptionService";
import { PreferencesContext } from "../../PreferenceContext";
import { PAGE_SIZE } from "../../services/common/const";
import {
  CreateQuickNotes,
  GetQuickNotesDetail,
  UpdateQuickNotes,
} from "../../services/QuickNotesService";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";
import ReactQuill from "react-quill";
import { CustomSelect } from "../../components/CustomFields/CustomSelect";
import { selectOption } from "../../../shared/Model/Common";
import { GetSpecialityList } from "../../services/SpecialityService";
import { GetDepartmentList } from "../../services/DepartmentService";

// SCHEMA FOR PARAMETER
interface Parameter {
  specialityId: string;
  departmentId: string;
  branchId: string;
  orgId: string;
  consumerId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  specialityId: "",
  departmentId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  orgId: "",
  consumerId: "",
};

// SCHEMA FOR FORM
interface formInitialValueTypes {
  quickNotesId: number;
  consumerId: number;
  organizationId: number;
  branchId: number;
  quickNotesTypeCode: String;
  quickNotesName: string;
  isDeleted: boolean;
  departmentId: selectOption | null;
  specialityId: selectOption | null;
  departmentName: selectOption | null;
  specialtyName: selectOption | null;
}

export default function QuickNotesSave(props: any) {
  // PROPS
  const { isForPopUp = false, handleClosePopUp, name, quickNoteType, autoSave = false, onAutoSaveComplete } = props;

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);

  // DEFAULT SCHEMA VALUE FOR FORM
  const initQuickNoteData: formInitialValueTypes = {
    quickNotesId: 0,
    consumerId: 0,
    organizationId: 0,
    branchId: 0,
    quickNotesTypeCode: quickNoteType || "",
    quickNotesName: name?.toUpperCase() || "",
    isDeleted: false,
    departmentId:
      userContext?.departmentId > 0
        ? {
          label: userContext?.departmentName || "",
          value: userContext?.departmentId?.toString(),
        }
        : null,
    specialityId:
      userContext?.specialityId > 0
        ? {
          label: userContext?.specialityName || "",
          value: userContext?.specialityId?.toString(),
        }
        : null,
    departmentName: null,
    specialtyName: null,
  };

  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let curRawQuickNotesId = urlParams.get("q");
  let QuickNotesType: any = urlParams.get("type");

  let curQuickNotesId = 0;
  if (
    ((curRawQuickNotesId !== null && curRawQuickNotesId !== undefined) ||
      (QuickNotesType !== null && QuickNotesType !== undefined)) &&
    isForPopUp === false
  ) {
    let decId = edService.decrypt(curRawQuickNotesId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      curQuickNotesId = +decId;
    }
  } else {
    if (urlParams.size > 0 && isForPopUp === false) {
      // navigate("/not-found");
    }
  }

  if (isForPopUp && quickNoteType) {
    QuickNotesType = quickNoteType;
  }
  // VARIABLES -- STATIC DATA

  const OptionInstructionType = (Type) => {
    let arr: { label: string; value: any; placeHolder: any }[] = [];
    switch (Type) {
      case "chief-complaint":
        arr = [
          {
            label: "Chief Complaint Notes",
            value: "CHIEF_COMPLAINT_NOTE",
            placeHolder: "Enter Chief Complaint Note",
          },
        ];
        break;

      case "history":
        arr = [
          {
            label: "History Notes",
            value: "PATIENT_HISTORY_PAST_HISTORY_NOTE",
            placeHolder: "Enter History Note",
          },
        ];
        break;

      case "examination":
        arr = [
          {
            label: "Examination Notes",
            value: "EXAMINATION_GENERAL_EXAMINATION_VITAL_PARAMETERS_NOTE",
            placeHolder: "Enter Examination Note",
          },
        ];
        break;

      case "investigation":
        arr = [
          {
            label: "Investigation Notes",
            value: "INVESTIGATION_NOTE",
            placeHolder: "Enter Investigation Note",
          },
        ];
        break;

      case "diagnosis":
        arr = [
          {
            label: "Diagnosis Notes",
            value: "DIAGNOSIS_NOTE",
            placeHolder: "Enter Diagnosis Note",
          },
        ];
        break;

      case "prescription":
        arr = [
          {
            label: "Prescription Notes",
            value: "PRESCRIPTION_NOTE",
            placeHolder: "Enter Prescription Note",
          },
        ];
        break;

      case "follow-up":
        arr = [
          {
            label: "Follow-up Notes",
            value: "FOLLOW_UP_NOTE",
            placeHolder: "Enter Follow-up Note",
          },
        ];
        break;

      case "general-instruction":
        arr = [
          {
            label: "General Instruction Notes",
            value: "GENERAL_INSTRUCTION_NOTE",
            placeHolder: "Enter General Instruction Note",
          },
        ];
        break;

      default:
        arr = [];
        break;
    }
    return arr;
  };

  const quickNotesTypeCode = OptionInstructionType(QuickNotesType).find(
    (x) => x.value
  );

  // IF NOT FIND THEN NAVIGATE TO NOT FOUND PAGE  --- PENDING -- ASK TO SIR ABOUT THIS

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [departmentData, setdepartmentData] = useState<any[]>([]);
  const [specialityData, setspecialityData] = useState<any[]>([]);
  const [param, setParam] = useState(parameters);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    quickNotesId: Yup.number().nullable(),
    organizationId: Yup.number().required(),
    consumerId: Yup.number().required(),
    branchId: Yup.number().required(),
    quickNotesTypeCode: Yup.string(),
    quickNotesName: Yup.string().required("QuickNotes is required"),
    isDeleted: Yup.boolean(),
    departmentId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Department is required"),
    specialityId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Speciality  is required"),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initQuickNoteData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  // GET Quick Note Name DETAIL
  const getQuickNotesDetailByID = async () => {
    let QuickNote: any;
    let params = new URLSearchParams();
    params.append("QuickNotesId", curQuickNotesId?.toString());
    params.append("ConsumerId", userContext?.consumerId?.toString());
    params.append("OrganizationId", userContext?.organizationId?.toString());
    params.append("BranchId", userContext?.branchId?.toString());

    try {
      const res: any = await GetQuickNotesDetail(params);
      if (res?.data?.data) {
        QuickNote = res.data.data;
        methods.reset({
          ...initQuickNoteData,
          quickNotesId: QuickNote?.quickNotesId || 0,
          quickNotesTypeCode: QuickNote?.quickNotesTypeCode || "",
          quickNotesName: QuickNote?.quickNotesName,
          departmentId: QuickNote?.departmentId
            ? {
              value: QuickNote?.departmentId?.toString(),
              label: QuickNote?.departmentName || "",
            }
            : null,
          specialityId: QuickNote?.specialityId
            ? {
              value: QuickNote?.specialityId?.toString(),
              label: QuickNote?.specialityName || "",
            }
            : null,
        });
      }
      return QuickNote;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE INSTRUCTION
  const saveQuickNotes = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";

      let formData = {
        quickNotesId: value.quickNotesId.toString(),
        consumerId: userContext.consumerId.toString(),
        organizationId: userContext.organizationId.toString(),
        branchId: userContext.branchId.toString(),
        quickNotesTypeCode: quickNotesTypeCode?.value?.toString(),
        quickNotesName: value.quickNotesName?.toString()?.toUpperCase(),
        departmentId: value?.departmentId?.value || null,
        departmentName: value?.departmentId?.label || null,
        specialityId: value?.specialityId?.value || null,
        specialityName: value?.specialityId?.label || null,
        DataManagedBy: userContext.loginDetail.userId,

      };
      if (curQuickNotesId > 0) {
        result = await UpdateQuickNotes(formData);
      } else {
        result = await CreateQuickNotes(formData);

      }
      if (result?.data) {

        if (!isForPopUp) {
          navigate(-1);
        } else {
          if (result?.data) {
            handleClosePopUp("QUICK_NOTES_CREATE", result?.data);

          }
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // GET DEPARTMENT
  async function fetchDepartment() {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext?.consumerId?.toString());
      params.append("orgId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const departmentData: any = await GetDepartmentList(params);
      if (departmentData?.data?.data?.departmentColl) {
        let departmentCollection = departmentData.data.data.departmentColl;
        setdepartmentData(departmentCollection);
        return departmentCollection;
      }
      return [];
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
      return [];
    }
  }

  // GET SPECIALITY
  async function fetchSpeciality(departmentIds: string) {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext?.consumerId?.toString());
      params.append("orgId", userContext?.organizationId?.toString());
      params.append("branchId", userContext?.branchId?.toString());
      params.append("departmentIds", departmentIds);
      params.append("mode", "DROPDOWN".toString());
      const specialityData: any = await GetSpecialityList(params);
      if (specialityData?.data?.data?.specialityColl) {
        let specialityCollection = specialityData.data.data.specialityColl;
        setspecialityData(specialityCollection);
        return specialityCollection;
      }
      return [];
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
      return [];
    }
  }

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveQuickNotes(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      const deptCollection = await fetchDepartment();
      if (curQuickNotesId > 0) {
        const detail = await getQuickNotesDetailByID();
        if (detail?.departmentId) {
          await fetchSpeciality(detail.departmentId.toString());
        }
      } else {
        if (deptCollection?.length === 1) { 
          const singleDept = deptCollection[0];
          methods.setValue(
            "departmentId",
            { label: singleDept.label, value: singleDept.value },
            { shouldValidate: true },
          );

          const specCollection = await fetchSpeciality(singleDept.value);
          if (specCollection?.length === 1) {
            const singleSpec = specCollection[0];
            methods.setValue(
              "specialityId",
              { label: singleSpec.label, value: singleSpec.value },
              { shouldValidate: true },
            );
          }
        } else {
          await fetchSpeciality("");
        }
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curQuickNotesId]);

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <>
            <Grid container className="page-header">
              <Grid item xs={6} sm={6} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    alignItems: "center",
                  }}>
                  <CustomBreadcrumb />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6} md={6}>
                <Box className="page-header-icon">
                  <Button
                    color="primary"
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}>
                    Back
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} className={"group-container"}>
                <Grid item container xs={12} spacing={2}>
                  {/* <Grid item xs={12}>
                    <FormControl fullWidth className="from-controller">
                      <FormLabel className="fileds-label-text">
                        {quickNotesTypeCode?.label?.toString()}
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-box">
                        <Controller
                          control={methods.control}
                          name={"quickNotesName"}
                          render={({ field }) => (
                            <ReactQuill
                              {...field}
                              theme="snow"
                              value={methods?.getValues()?.quickNotesName}
                              className={`react-quill-alignment`}
                              placeholder={
                                quickNotesTypeCode?.placeHolder || ""
                              }
                              modules={{
                                toolbar: [["bold", "italic", "underline"]],
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.quickNotesName?.message}
                    </Typography>
                  </Grid> */}
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.departmentId
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Department *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="departmentId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"departmentId"}
                              FieldName={"departmentId"}
                              options={departmentData}
                              openMenuOnFocus
                              isClearable={departmentData?.length > 1}
                              isSearchable
                              menuPortalTarget={document.querySelector(
                                "body"
                              )}
                              handleChange={async (e: any) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.departmentId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "departmentId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                  methods.setValue("specialityId", null, {
                                    shouldValidate: true,
                                  });
                                  methods.setValue("specialityName", null, {
                                    shouldValidate: true,
                                  });
                                }
                                if (e?.value) {
                                  setIsLoading(true);
                                  await fetchSpeciality(e.value);
                                  setIsLoading(false);
                                } else {
                                  setspecialityData([]);
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.departmentId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.specialityId
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Speciality *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="specialityId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"specialityId"}
                              FieldName={"specialityId"}
                              options={specialityData}
                              openMenuOnFocus
                              isClearable={specialityData?.length > 1}
                              isSearchable
                              getOptionValue={(option: any) => option.value}
                              getOptionLabel={(option: any) => option.label}
                              menuPortalTarget={document.querySelector(
                                "body"
                              )}
                              handleChange={async (e: any) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.specialityId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "specialityId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.specialityId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth className="from-controller">
                      <FormLabel className="fileds-label-text">
                        {quickNotesTypeCode?.label?.toString()}
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-box">
                        <Controller
                          control={methods.control}
                          name="quickNotesName"
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              variant="outlined"
                              multiline
                              rows={3}
                              placeholder={
                                quickNotesTypeCode?.placeHolder || ""
                              }
                              autoFocus
                              inputProps={{ maxLength: 500, style: { textTransform: "uppercase" } }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  methods.handleSubmit(onSubmit)();
                                }
                              }}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.quickNotesName?.message}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={isLoading || isLoading}>
                    {curQuickNotesId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}
                    disabled={isLoading || isLoading}>
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
}
